import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const SUGGESTIONS = ["peace", "meditation", "service", "unity", "आत्मा"];

const STEPS = [
  {
    title: "Find a talk",
    body: "Search for a word or idea, or browse by theme or speaker.",
  },
  {
    title: "Read along",
    body: "Open a talk and tap any line to jump the video to that moment.",
  },
  {
    title: "Collect quotes",
    body: "Save the lines you like. Your quotes export as a ready-to-paste list.",
  },
];

const BROWSE = [
  {
    to: "/library",
    title: "All talks",
    body: "Filter and sort every discourse.",
  },
  {
    to: "/themes",
    title: "By theme",
    body: "Three threads through the library.",
  },
  {
    to: "/speakers",
    title: "By speaker",
    body: "The teachers, and their words.",
  },
];

export function Home() {
  const [query, setQuery] = useState("");
  const navigate = useNavigate();

  const goSearch = (text: string) => {
    const trimmed = text.trim();
    navigate(
      trimmed.length > 0
        ? `/search?q=${encodeURIComponent(trimmed)}`
        : "/search",
    );
  };

  return (
    <div className="mx-auto max-w-3xl">
      <header className="pt-4 text-center sm:pt-8">
        <h1 className="font-display text-4xl text-warm-brown sm:text-5xl">
          Find a line across 1,548 talks
        </h1>
        <p className="mx-auto mt-4 max-w-xl text-balance leading-relaxed text-warm-brown-light">
          Every recorded discourse of the Sansthan, transcribed and searchable.
          Open a talk to hear it, and collect quotes to share.
        </p>
      </header>

      <form
        className="mt-8"
        onSubmit={(event) => {
          event.preventDefault();
          goSearch(query);
        }}
      >
        <div className="flex items-center gap-2 border-b border-hairline-strong pb-1 focus-within:border-gold">
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            className="shrink-0 text-warm-gray"
            aria-hidden="true"
          >
            <circle cx="11" cy="11" r="7" />
            <path d="m21 21-4.3-4.3" strokeLinecap="round" />
          </svg>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Search for a word or idea, like peace or seva"
            className="w-full bg-transparent py-2 font-serif text-xl text-warm-brown outline-none placeholder:text-warm-gray/70"
            aria-label="Search the discourses"
          />
          <button
            type="submit"
            className="shrink-0 rounded-full bg-terra px-4 py-1.5 text-sm font-medium text-warm-white hover:bg-gold"
          >
            Search
          </button>
        </div>
      </form>

      <div className="mt-3 flex flex-wrap items-center gap-2 text-sm">
        <span className="text-warm-gray">Try</span>
        {SUGGESTIONS.map((term) => (
          <button
            key={term}
            type="button"
            onClick={() => goSearch(term)}
            className="rounded-full border border-hairline px-3 py-1 text-warm-brown transition-colors hover:border-gold/50 hover:text-terra"
          >
            {term}
          </button>
        ))}
      </div>

      <ol className="mt-12 grid gap-6 sm:grid-cols-3">
        {STEPS.map((step, index) => (
          <li key={step.title}>
            <div className="font-serif text-lg italic text-gold">
              {index + 1}
            </div>
            <h2 className="mt-1 font-display text-lg text-warm-brown">
              {step.title}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-warm-gray">
              {step.body}
            </p>
          </li>
        ))}
      </ol>

      <div className="mt-12 border-t border-hairline pt-8">
        <p className="eyebrow mb-4">Or browse</p>
        <div className="grid gap-4 sm:grid-cols-3">
          {BROWSE.map((card) => (
            <Link
              key={card.to}
              to={card.to}
              className="plate flex flex-col gap-1 p-4 transition-transform hover:-translate-y-0.5"
            >
              <span className="font-display text-lg text-warm-brown">
                {card.title}
              </span>
              <span className="text-sm text-warm-gray">{card.body}</span>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}
